import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { SimplebarAngularComponent } from 'simplebar-angular';
import { environment } from 'src/environments/environment';
import { FirebaseMessagingService } from 'src/app/core/services/firebase-messaging.service';
import { NotificationsService } from 'src/app/core/backend/common/services/notifications.service';
import { ChatNotificationBridgeService } from 'src/app/core/services/chat-notification-bridge.service';
import { AuthenticationService } from 'src/app/core/services/auth.service';
import { Subject, takeUntil, interval } from 'rxjs';
import { take } from 'rxjs/operators';
import { MessagePayload } from 'firebase/messaging';
import { TranslateService } from '@ngx-translate/core';
import { SupportPanelService } from 'src/app/support/support-panel.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'sahely-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.scss'],
  standalone: false
})
export class NotificationsComponent implements OnInit, OnDestroy {
  @ViewChild('simplebar') simplebar?: SimplebarAngularComponent;
  NotificationType: any = {};
  domain = environment.api;
  now = new Date();
  notifs: any[] = [];
  $total = 0;
  $page = 1;
  $pageSize = 10;
  acc: any | null;
  unreadCount = 0;
  gettingNotifs = false;
  bell = new Audio('assets/audio/noti.wav');

  private destroy$ = new Subject<void>();

  // Firebase notification properties
  fcmToken: string | null = null;
  notificationsSupported = false;
  notificationPermission: NotificationPermission = 'default';

  scrollbarOptions = {
    autoHide: true,
    direction: 'rtl',
  };

  constructor(
    private router: Router,
    private firebaseMessaging: FirebaseMessagingService,
    private notificationsService: NotificationsService,
    private chatBridge: ChatNotificationBridgeService,
    private authService: AuthenticationService,
    private translate: TranslateService,
    private supportPanel: SupportPanelService,
  ) {
    // Check if Firebase Cloud Messaging is supported
    this.notificationsSupported = this.firebaseMessaging.isNotificationSupported();
    this.notificationPermission = this.firebaseMessaging.getPermissionStatus();
  }

  ngOnInit(): void {
    // Initialize Firebase Cloud Messaging
    this.initializeFirebaseMessaging();

    // The identity subscription below loads the list once the user is known.
    // A second call here duplicated /notifications and /unread-count on every boot.

    // Real-time chat notifications — keep retrying until listeners are live.
    this.chatBridge.newMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const senderName = event.senderName || 'المستخدم';
        const item = {
          id: `chat_${Date.now()}`,
          title: this.translate.instant('UI_MESSAGES.NOTIFICATIONS.NEW_MESSAGE_FROM', { senderName }),
          message: event.content,
          type: 'chat_message',
          read: false,
          createdate: event.created_at.toISOString(),
          route: 'chat_message',
          module: 'chat',
          payload: { chatId: event.chatId, chat_id: event.chatId, sender_name: senderName },
        };
        this.notifs.unshift(item);
        this.unreadCount++;
        this.playNotificationSound();
        if (this.shouldShowChatToast()) {
          this.showChatToast(senderName);
        }
      });

    this.bootChatBridge();
  }

  private bootChatBridge(): void {
    const startBridge = () => {
      this.chatBridge.stopListening();
      this.chatBridge.ensureListening();
    };

    startBridge();

    this.authService.identity()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.reloadBranchScopedNotifications();
        startBridge();
      });

    interval(2000)
      .pipe(take(15), takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.chatBridge.isActive()) {
          startBridge();
        }
      });
  }

  private reloadBranchScopedNotifications(): void {
    this.notifs = this.notifs.filter((item) => item.type === 'chat_message');
    this.$page = 1;
    this.getAll(true);
    this.getUnreadCount();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.simplebar?.SimpleBar) {
        const scrollElement = this.simplebar.SimpleBar.getScrollElement();
        if (scrollElement) {
          scrollElement.onscroll = (event: Event) => this.handleScroll(event);
        }
      }
    }, 10);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize Firebase Cloud Messaging
   */
  private async initializeFirebaseMessaging(): Promise<void> {
    if (!this.notificationsSupported) {
      console.warn('Firebase Cloud Messaging is not supported in this browser');
      return;
    }

    try {
      // Request notification permissions
      if (this.notificationPermission === 'default') {
        const token = await this.firebaseMessaging.requestPermission();
        if (token) {
          this.fcmToken = token;
          this.notificationPermission = 'granted';

          // TODO: Send token to backend to associate with user
          // await this.saveTokenToBackend(token);
          console.log('FCM Token ready to be saved to backend:', token);
        }
      } else if (this.notificationPermission === 'granted') {
        // Get existing token
        const token = await this.firebaseMessaging.getToken();
        if (token) {
          this.fcmToken = token;
          console.log('Existing FCM Token:', token);
        }
      }

      // Listen to foreground messages
      this.firebaseMessaging.listenToMessages()
        .pipe(takeUntil(this.destroy$))
        .subscribe((payload: MessagePayload) => {
          console.log('Firebase notification received:', payload);

          if (!this.notificationBelongsToScope(payload.data as Record<string, unknown> | undefined)) {
            return;
          }

          // Play notification sound
          this.playNotificationSound();

          // Add notification to local list
          this.addFirebaseNotificationToList(payload);
          if (payload.data?.['module'] === 'support_ticket') {
            this.supportPanel.requestUnreadRefresh();
          }

          // Show browser notification
          if (payload.notification) {
            this.firebaseMessaging.showNotification(
              payload.notification.title || 'New Notification',
              {
                body: payload.notification.body,
                icon: payload.notification.icon || '/assets/images/logo-sm.png',
                badge: '/assets/images/logo-sm.png',
                data: payload.data
              }
            );
          }
        });

    } catch (error) {
      console.error('Error initializing Firebase Messaging:', error);
    }
  }

  /**
   * Add Firebase notification to local list
   */
  private addFirebaseNotificationToList(payload: MessagePayload): void {
    const notification = {
      id: Date.now().toString(),
      title: payload.notification?.title || 'Notification',
      message: payload.notification?.body || '',
      type: payload.data?.type || 'general',
      read: false,
      createdAt: new Date().toISOString(),
      data: payload.data
    };

    this.notifs.unshift(notification);
    this.unreadCount++;
  }

  /** Skip toast when the chats tab is already open — ChatsComponent shows its own toast. */
  private shouldShowChatToast(): boolean {
    const tree = this.router.parseUrl(this.router.url);
    const tab = tree.queryParams['tab'];
    const chatId = tree.queryParams['chatId'];
    if (!this.router.url.includes('/communication')) {
      return true;
    }
    return tab !== 'chats' && !chatId;
  }

  private showChatToast(senderName: string): void {
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'info',
      title: this.translate.instant('UI_MESSAGES.CHAT.NEW_MESSAGE_FROM', { senderName }),
      showConfirmButton: false,
      showCloseButton: true,
      timer: 4000,
      timerProgressBar: true,
      width: 'auto',
      padding: '0.6rem 1rem',
    });
  }

  /**
   * Play notification sound
   */
  private playNotificationSound(): void {
    setTimeout(() => {
      this.bell.play().catch(err => {
        console.warn('Could not play notification sound:', err);
      });
    }, 300);
  }

  /**
   * TODO: Save FCM token to backend
   * This method should be called after getting the FCM token
   */
  private async saveTokenToBackend(token: string): Promise<void> {
    // TODO: Implement API call to save token
    // Example:
    // await this.notificationService.saveFcmToken({ token }).toPromise();
    console.log('TODO: Save FCM token to backend:', token);
  }

  handleScroll(event: any) {
    const simplebarElement = this.simplebar?.SimpleBar.getScrollElement();
    if (!simplebarElement) return;

    // Check if the user has scrolled to the bottom of the scrollbar
    if (simplebarElement.scrollTop + simplebarElement.clientHeight === simplebarElement.scrollHeight) {
      // Load more notifications
      if (this.$page * this.$pageSize < this.$total) {
        this.$page++;
        this.getAll();
      }
    }
  }

  /**
   * Get all notifications from backend
   */
  getAll(reset = false) {
    if (reset) {
      this.notifs = this.notifs.filter((item) => item.type === 'chat_message');
      this.$page = 1;
    }

    this.gettingNotifs = true;
    this.notificationsService.apiV1NotificationsGet({
      page: String(this.$page),
      per_page: String(this.$pageSize)
    }).subscribe({
      next: (res: any) => {
        this.gettingNotifs = false;
        const items = (res?.data?.notifications || [])
          .map((n: any) => ({
            id: n.id,
            title: n.title_ar || n.title_en || n.title || 'إشعار',
            message: n.message_ar || n.message_en || n.message || '',
            type: n.key_id,
            read: n.seen === 1 || n.read === true,
            createdate: n.created_at,
            route: n.route,
            payload: n.payload,
            module: n.module,
            module_id: n.module_id,
          }))
          .filter((n: any) => this.notificationBelongsToScope(n));

        this.notifs.push(...items);
        this.$total = res?.data?.pagination?.total || 0;
        this.syncUnreadCount();
      },
      error: () => {
        this.gettingNotifs = false;
      }
    });
  }

  /**
   * Mark notification as read
   */
  readNotification(not: any) {
    if (not.read) return;

    not.read = true;
    this.unreadCount = Math.max(0, this.unreadCount - 1);

    // Local chat notifications have no backend record — skip API call
    if (not.type === 'chat_message') return;

    this.notificationsService.apiV1NotificationsIdReadPut({
      id: String(not.id)
    }).subscribe({
      error: () => {
        this.unreadCount++;
        not.read = false;
      }
    });
  }

  /**
   * Mark all notifications as read
   */
  readAll() {
    const prevCount = this.unreadCount;
    const prevNotifs = this.notifs.map(n => ({ ...n }));
    this.unreadCount = 0;
    this.notifs = this.notifs.map(n => ({ ...n, read: true }));

    this.notificationsService.apiV1NotificationsReadAllPut().subscribe({
      error: () => {
        this.unreadCount = prevCount;
        this.notifs = prevNotifs;
      }
    });
  }

  /**
   * Get unread notification count from backend
   */
  getUnreadCount() {
    this.notificationsService.apiV1NotificationsUnreadCountGet().subscribe({
      next: () => {
        this.syncUnreadCount();
      }
    });
  }

  private syncUnreadCount(): void {
    this.unreadCount = this.notifs.filter((item) => !item.read).length;
  }

  private notificationBelongsToScope(source: Record<string, unknown> | null | undefined): boolean {
    if (!source) {
      return true;
    }

    if (
      source['type'] === 'chat_message'
      || source['route'] === 'chat_message'
      || source['module'] === 'chat'
    ) {
      return true;
    }

    if (!this.authService.isBranchScopedContext()) {
      return true;
    }

    const scopeId = this.authService.getActiveAcademyScopeId();
    if (!scopeId) {
      return true;
    }

    const academyId = this.extractAcademyId(source);
    if (!academyId) {
      return true;
    }

    return academyId === scopeId;
  }

  private extractAcademyId(source: Record<string, unknown> | null | undefined): number {
    if (!source) {
      return 0;
    }

    const payload = typeof source['payload'] === 'string'
      ? this.parsePayload(String(source['payload']))
      : (source['payload'] as Record<string, unknown> | null);

    const candidates = [
      source['academy_id'],
      source['academyId'],
      source['branch_id'],
      source['branchId'],
      payload?.['academy_id'],
      payload?.['academyId'],
      payload?.['branch_id'],
      payload?.['branchId'],
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate ?? 0);
      if (Number.isInteger(parsed) && parsed > 0) {
        return parsed;
      }
    }

    const chatId = String(source['chatId'] ?? source['chat_id'] ?? payload?.['chat_id'] ?? payload?.['chatId'] ?? '');
    const match = chatId.match(/^ac(\d+)_/);
    if (match) {
      return Number(match[1]);
    }

    return 0;
  }

  /**
   * Redirect to notification detail
   */
  redirectTo(not: any) {
    if (!not.read) {
      this.readNotification(not);
    }

    if ((not.module ?? not.data?.module) === 'support_ticket') {
      // Support replies open the ticket in the launcher panel (FEAT-057 REQ-031).
      this.supportPanel.open(this.resolveSupportRef(not));
      return;
    }

    if (not.route === 'chat_message' || not.module === 'chat') {
      const chatId = this.resolveChatId(not);
      if (!chatId) {
        this.router.navigate(['/communication'], { queryParams: { tab: 'chats' } });
        return;
      }

      this.router.navigate(['/communication'], {
        queryParams: { tab: 'chats', chatId },
        queryParamsHandling: 'merge',
      });
    }
  }

  /** The ticket ref from a stored notification (`payload`) or a push message (`data`). */
  private resolveSupportRef(not: any): string {
    const raw = not.payload ?? not.data?.payload ?? not.data;
    const payload = typeof raw === 'string' ? this.parsePayload(raw) : raw;
    return String(payload?.ref ?? not.data?.ref ?? '');
  }

  private resolveChatId(not: any): string | null {
    const payload = typeof not.payload === 'string'
      ? this.parsePayload(not.payload)
      : not.payload;
    const chatId = payload?.chatId ?? payload?.chat_id;
    return chatId ? String(chatId) : null;
  }

  private parsePayload(raw: string): Record<string, unknown> | null {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
    } catch {
      return null;
    }
  }
}
